import React from 'react';
import { useTranslation } from 'react-i18next';

interface CompletionMethod {
  id: string;
  label: string;
}

interface AlignmentToggleProps {
  isAligned: boolean;
  showOriginal: boolean;
  appliedMethod: string | null;
  selectedMethod: string;
  completionMethods: CompletionMethod[];
  onToggle: () => void;
  onRevert: () => void;
}

export const AlignmentToggle: React.FC<AlignmentToggleProps> = ({
  isAligned,
  showOriginal,
  appliedMethod,
  selectedMethod,
  completionMethods,
  onToggle,
  onRevert
}) => {
  const { t } = useTranslation();

  if (!isAligned) return null;

  const appliedMethodLabel = completionMethods.find(m => m.id === appliedMethod)?.label || appliedMethod;

  return (
    <div className="border-t pt-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm text-swiss-charcoal">
          <span className="font-medium">{t('dataAlignment.toggle.dataStatus')}:</span>
          <span className="ml-2 text-green-600">✓ {t('dataAlignment.toggle.completedWith', { method: appliedMethodLabel })}</span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onToggle}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            {showOriginal ? t('dataAlignment.toggle.showCompleted') : t('dataAlignment.toggle.showOriginal')}
          </button>
          <button
            onClick={onRevert}
            className="px-3 py-1 text-sm border border-red-300 text-red-600 rounded-md hover:bg-red-50 transition-colors"
          >
            {t('dataAlignment.toggle.clear')}
          </button>
        </div>
      </div>
      <div className="text-xs text-swiss-stone bg-blue-50 p-2 rounded mb-2">
        {showOriginal ?
          t('dataAlignment.toggle.viewingOriginal') :
          t('dataAlignment.toggle.viewingCompleted', { method: appliedMethodLabel })}
      </div>
      {appliedMethod !== selectedMethod && (
        <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
          {t('dataAlignment.toggle.differentMethodNote')}
        </div>
      )}
    </div>
  );
};